import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    GridDirection,
    InputLabel,
} from '@mui/material';
import get from 'lodash/get';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { BaseFieldProps } from './BaseField';
import { getErrorMessage } from '@utils/form';

interface MultiCheckboxFieldProps
    extends Omit<BaseFieldProps, 'label' | 'placeholder' | 'rows' | 'type'> {
    checkAllLabel?: string;
    direction?: GridDirection;
    label?: ReactNode;
    options: { [key: string]: string };
}

const MultiCheckBoxField: FC<MultiCheckboxFieldProps> = ({
    control,
    checkAllLabel,
    disabled,
    direction = 'column',
    formDisabled,
    formState,
    label = '',
    name,
    options,
    required,
    watch,
}) => {
    const { t } = useTranslation('common');
    const selectedOptions = (watch(name) || []) as string[];
    const values = Object.keys(options);

    const [allChecked, setAllChecked] = useState(selectedOptions.length === values.length);

    const error = get(formState.errors, name) as FieldError;

    const handleCheckAll = (checked: boolean) => {
        setAllChecked(checked);

        return checked ? values : [];
    };

    const handleSelect = (checkedName: string): string[] => {
        const newNames = selectedOptions?.includes(checkedName)
            ? selectedOptions.filter((option) => option !== checkedName)
            : [...(selectedOptions || []), checkedName];

        const newAllCheckedValue = newNames.length === values.length;
        if (allChecked !== newAllCheckedValue) {
            setAllChecked(newAllCheckedValue);
        }

        return newNames;
    };

    const errorMessage = getErrorMessage({ error, t });

    return (
        <>
            {label && (
                <InputLabel
                    required={Boolean(required)}
                    sx={
                        disabled || formDisabled
                            ? {
                                  color: 'text.disabled',
                              }
                            : null
                    }
                >
                    {label}
                </InputLabel>
            )}
            <FormGroup>
                <Controller
                    control={control}
                    name={name}
                    render={({ field }) => (
                        <Grid direction={direction} spacing={2} container>
                            {checkAllLabel && (
                                <Grid key={`${name}-check-all`} item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allChecked}
                                                color="primary"
                                                data-test-id={`${name}-check-all`}
                                                disabled={disabled || formDisabled}
                                                indeterminate={Boolean(
                                                    selectedOptions.length &&
                                                        selectedOptions.length !== values.length,
                                                )}
                                                name={name}
                                                onChange={(_, checked) =>
                                                    field.onChange(handleCheckAll(checked))
                                                }
                                            />
                                        }
                                        label={checkAllLabel}
                                        sx={{
                                            '& span': {
                                                fontWeight: 'medium',
                                            },
                                        }}
                                    />
                                </Grid>
                            )}
                            {Object.entries(options).map(([value, optionLabel]) => (
                                <Grid key={`${name}-${value}`} item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedOptions.includes(value)}
                                                color="primary"
                                                data-test-id={`${optionLabel}-checkbox`}
                                                disabled={disabled || formDisabled}
                                                name={name}
                                                onChange={() => field.onChange(handleSelect(value))}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        '& + .MuiFormControlLabel-label': {
                                                            fontWeight: 'medium',
                                                        },
                                                    },
                                                }}
                                            />
                                        }
                                        label={optionLabel}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    rules={{ required }}
                />
                {errorMessage && (
                    <Box mt={1}>
                        <FormHelperText error>{errorMessage}</FormHelperText>
                    </Box>
                )}
            </FormGroup>
        </>
    );
};

export default MultiCheckBoxField;
